import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ItemCard from "@/components/ItemCard";
import { CATEGORIES, ALL_LOCATIONS } from "@/lib/constants";
import api from "@/lib/api";

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    category: searchParams.get("category") || "",
    location: searchParams.get("location") || "",
    location_type: searchParams.get("location_type") || "",
    sort: searchParams.get("sort") || "newest",
    page: 1,
  });

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.category) params.category = filters.category;
    if (filters.location) params.location = filters.location;
    if (filters.location_type) params.location_type = filters.location_type;
    params.sort = filters.sort;
    params.page = filters.page;
    params.limit = 20;

    api.get("/items", { params })
      .then((res) => { setItems(res.data.items); setTotal(res.data.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filters]);

  const updateFilter = (key, value) => {
    const v = value === "all" ? "" : value;
    setFilters((prev) => ({ ...prev, [key]: v, page: 1 }));
    if (v) {
      searchParams.set(key, v);
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setFilters({ search: "", category: "", location: "", location_type: "", sort: "newest", page: 1 });
    setSearchParams({});
  };

  const hasActiveFilters = filters.search || filters.category || filters.location || filters.location_type;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="browse-page">
      {/* Search + Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            data-testid="browse-search-input"
            type="text"
            placeholder="Search items..."
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            className="w-full pl-11 pr-4 h-12 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-coral-light focus:border-coral bg-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}
            className="rounded-xl h-10 gap-2 border-stone-200" data-testid="toggle-filters-btn">
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </Button>
          <Select value={filters.sort} onValueChange={(v) => updateFilter("sort", v)}>
            <SelectTrigger className="w-40 h-10 rounded-xl border-stone-200" data-testid="sort-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-2xl border border-stone-200 p-6 mb-8 animate-fade-up" data-testid="filters-panel">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Category</label>
              <Select value={filters.category} onValueChange={(v) => updateFilter("category", v)}>
                <SelectTrigger className="rounded-xl border-stone-200" data-testid="filter-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Location</label>
              <Select value={filters.location} onValueChange={(v) => updateFilter("location", v)}>
                <SelectTrigger className="rounded-xl border-stone-200" data-testid="filter-location">
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {ALL_LOCATIONS.map((l) => (
                    <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Region</label>
              <Select value={filters.location_type} onValueChange={(v) => updateFilter("location_type", v)}>
                <SelectTrigger className="rounded-xl border-stone-200" data-testid="filter-region">
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  <SelectItem value="caribbean">Caribbean</SelectItem>
                  <SelectItem value="us">United States</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="mt-4 text-stone-500 hover:text-stone-700" data-testid="clear-filters-btn">
              <X className="w-3 h-3 mr-1" /> Clear all filters
            </Button>
          )}
        </div>
      )}

      {/* Results Header */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-stone-500" data-testid="browse-results-count">
          {total} {total === 1 ? "item" : "items"} found
        </p>
        {hasActiveFilters && (
          <div className="flex gap-2 flex-wrap">
            {filters.category && (
              <span className="text-xs bg-coral-light text-coral px-2.5 py-1 rounded-full flex items-center gap-1">
                {filters.category} <X className="w-3 h-3 cursor-pointer" onClick={() => updateFilter("category", "")} />
              </span>
            )}
            {filters.location && (
              <span className="text-xs bg-coral-light text-coral px-2.5 py-1 rounded-full flex items-center gap-1">
                {filters.location} <X className="w-3 h-3 cursor-pointer" onClick={() => updateFilter("location", "")} />
              </span>
            )}
          </div>
        )}
      </div>

      {/* Results Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="rounded-2xl border border-stone-200 bg-white overflow-hidden animate-pulse">
              <div className="aspect-[4/3] bg-stone-100" />
              <div className="p-4 space-y-2">
                <div className="h-3 bg-stone-100 rounded w-1/2" />
                <div className="h-4 bg-stone-100 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((item) => (
            <ItemCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="text-center py-20" data-testid="browse-no-results">
          <p className="text-stone-500 text-lg mb-2">No items found</p>
          <p className="text-stone-400 text-sm">Try adjusting your filters or search terms</p>
          <Button variant="outline" onClick={clearFilters} className="mt-4 rounded-full" data-testid="browse-clear-search">
            Clear search
          </Button>
        </div>
      )}
    </div>
  );
}
